import { useEffect, useState } from "react";
import { hitApi, dateFormatter, exportCSVFile } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

export default function Commissions({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        hitApi({ url: "affiliate/commissions", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setRows(resp.data?.commissions || []);
        });
    }, []);

    const exportCsv = () => {
        if (!rows.length) return;
        const headers = { createdAt: "Date", type: "Type", amount: "Amount", currency: "Currency", rate: "Rate", status: "Status" };
        exportCSVFile(headers, rows.map(r => ({
            createdAt: dateFormatter(r.createdAt),
            type: r.type,
            amount: r.amount,
            currency: r.currency,
            rate: r.rate,
            status: r.status,
        })), "commissions");
    };

    /* Summary stats — assumes a single currency (the affiliate program uses
     * one currency at a time). Defaults to USD when there are no rows. */
    const currency = rows[0]?.currency || "USD";
    const sumOf = (filterFn) => rows.filter(filterFn).reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalEarned = sumOf(r => r.type === "earned");
    const totalClawback = Math.abs(sumOf(r => r.type !== "earned"));
    const available = sumOf(r => r.status === "available");
    const paid = sumOf(r => r.status === "paid");
    const pending = sumOf(r => r.status === "pending");
    const fmt = (n) => `${currency} ${Number(n).toFixed(2)}`;

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Commissions">
            {loading ? (
                <CommissionsSkeleton />
            ) : (
            <>
            {/* Stats summary */}
            <div className="pxl-aff-stats">
                <StatCard label="Total earned" value={fmt(totalEarned)} icon={<IconCash />}  tone="accent"  />
                <StatCard label="Available"    value={fmt(available)}   icon={<IconCheck2 />} tone="success" />
                <StatCard label="Pending"      value={fmt(pending)}     icon={<IconHourglass />} tone="warning" />
                <StatCard label="Paid out"     value={fmt(paid)}        icon={<IconBank />}   tone="default" />
            </div>

            <div className="pxl-aff-panel">
                <div className="pxl-aff-panel-header">
                    <div>
                        <h3 className="pxl-aff-panel-title">Commission events</h3>
                        <div className="pxl-aff-panel-sub">
                            Each row is a commission earned or clawed back.{" "}
                            {totalClawback > 0 && <>Lifetime clawback: <strong>{fmt(totalClawback)}</strong>.</>}
                        </div>
                    </div>
                    <button
                        className="pxl-aff-btn-export"
                        onClick={exportCsv}
                        disabled={!rows.length}
                        title={rows.length ? "Download all commissions as CSV" : "No data to export"}
                    >
                        <IconDownload /> Export CSV
                    </button>
                </div>

                <div className="pxl-aff-table-wrap">
                    <table className="pxl-aff-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Rate</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan="5" className="pxl-aff-table-state-cell">
                                    <EmptyState
                                        icon={<IconCash />}
                                        title="No commissions yet"
                                        text="Commission events appear here automatically as your referred customers complete purchases. Hang tight — your first sale could be moments away."
                                    />
                                </td></tr>
                            ) : rows.map(r => (
                                <tr key={r._id}>
                                    <td><span className="pxl-aff-cell-strong">{dateFormatter(r.createdAt)}</span></td>
                                    <td>
                                        {r.type === "earned"
                                            ? <span className="pxl-aff-pill pxl-aff-pill--success"><IconDot /> Earned</span>
                                            : <span className="pxl-aff-pill pxl-aff-pill--warning">Clawed back</span>}
                                    </td>
                                    <td>
                                        <span className={Number(r.amount) < 0 ? "pxl-aff-amt pxl-aff-amt--negative" : "pxl-aff-amt"}>
                                            {r.currency} {Number(r.amount).toFixed(2)}
                                        </span>
                                    </td>
                                    <td><span className="pxl-aff-cell-strong">{(Number(r.rate) * 100).toFixed(0)}%</span></td>
                                    <td>
                                        {r.status === "paid"
                                            ? <span className="pxl-aff-pill pxl-aff-pill--info">Paid</span>
                                            : r.status === "available"
                                                ? <span className="pxl-aff-pill pxl-aff-pill--success">Available</span>
                                                : <span className="pxl-aff-pill pxl-aff-pill--muted">Pending</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            </>
            )}
        </AffiliatePageLayout>
    );
}

/* ── Skeleton ───────────────────────────────────────────────────────────────── */
function CommissionsSkeleton() {
    return (
        <>
            {/* 4-card stat row */}
            <div className="pxl-aff-stats" style={{ marginBottom: 20 }}>
                {[0, 1, 2, 3].map(i => (
                    <div className="pxl-sk-card" key={i}>
                        <span className="pxl-sk" style={{ width: '55%', height: 10, display: 'block' }} />
                        <span className="pxl-sk" style={{ width: '45%', height: 26, display: 'block', marginTop: 12 }} />
                    </div>
                ))}
            </div>

            {/* Table panel */}
            <div className="pxl-aff-panel">
                <div className="pxl-aff-panel-header">
                    <span className="pxl-sk" style={{ width: 140, height: 13, display: 'inline-block' }} />
                </div>
                <div className="pxl-aff-table-wrap">
                    <table className="pxl-aff-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Rate</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[0, 1, 2, 3, 4].map(i => (
                                <tr key={i}>
                                    <td><span className="pxl-sk" style={{ width: '65%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: '40%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */
function StatCard({ label, value, icon, tone = "default" }) {
    return (
        <div className={`pxl-aff-stat pxl-aff-stat--${tone}`}>
            <div className="pxl-aff-stat-head">
                <span className="pxl-aff-stat-label">{label}</span>
                <span className="pxl-aff-stat-icon">{icon}</span>
            </div>
            <div className="pxl-aff-stat-value">{value}</div>
        </div>
    );
}
function EmptyState({ icon, title, text }) {
    return (
        <div className="pxl-aff-empty">
            <div className="pxl-aff-empty-icon">{icon}</div>
            <div className="pxl-aff-empty-title">{title}</div>
            <p className="pxl-aff-empty-text">{text}</p>
        </div>
    );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconCash()      { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>; }
function IconCheck2()    { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function IconHourglass() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>; }
function IconBank()      { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="21" x2="21" y2="21"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="5 6 12 3 19 6"/><line x1="4" y1="10" x2="4" y2="21"/><line x1="20" y1="10" x2="20" y2="21"/><line x1="8" y1="14" x2="8" y2="17"/><line x1="12" y1="14" x2="12" y2="17"/><line x1="16" y1="14" x2="16" y2="17"/></svg>; }
function IconDownload()  { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function IconDot()       { return <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>; }
